import { useEffect } from "react";
import KennesawHeroSection from "./components/HeroSection";
import KennesawStickyNav from "./components/StickyNav";
import KennesawProcessingTimeSection from "./components/ProcessingTimeSection";
import KennesawServicesSection from "./components/ServicesSection";
import KennesawLocalAreaSection from "./components/LocalAreaSection";
import KennesawTestimonialsSection from "./components/TestimonialsSection";
import KennesawAcceptanceFacilitiesSection from "./components/AcceptanceFacilitiesSection";
import KennesawComparisonSection from "./components/ComparisonSection";
import KennesawMistakesSection from "./components/MistakesSection";
import KennesawHowItWorksSection from "./components/HowItWorksSection";
import KennesawFAQSection, { faqs } from "./components/FAQSection";
import KennesawHelpfulResourcesSection from "./components/HelpfulResourcesSection";
import KennesawFinalCTA from "./components/FinalCTA";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

export default function KennesawPage() {
  useEffect(() => {
    document.title = "Emergency & Expedited Passport Kennesaw GA | Same-Day Service | Fast Passport Center";

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    const pageUrl = "https://fastpassportcenter.com/kennesaw/";

    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in Kennesaw, GA — as fast as 24 hours. Same-day courier processing available. A+ BBB rated, registered State Dept. courier serving Cobb County and northwest Atlanta metro travelers. Get your emergency passport fast!");
    setMeta("geo.placename", "Kennesaw, Georgia");
    setMeta("geo.region", "US-GA");
    setMeta("geo.position", "34.0234;-84.6155");
    setMeta("ICBM", "34.0234, -84.6155");
    setMeta("keywords", "expedited passport, emergency passport, Kennesaw passport, same day passport Kennesaw, fast passport Georgia, Cobb County passport");

    const servicesList = [
      { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
      { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
      { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
      { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
      { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
      { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
      { name: "Second Passport & Damaged", description: "Second passport issuance for frequent travelers and damaged passport replacement." },
    ];

    const graph = [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
          { "@type": "ListItem", position: 2, name: "Georgia", item: "https://fastpassportcenter.com/georgia/" },
          { "@type": "ListItem", position: 3, name: "Kennesaw Passport Services", item: pageUrl },
        ],
      },
      {
        "@type": "LocalBusiness",
        name: "Fast Passport Center",
        description: "Fast Passport Center does not have a physical office in Kennesaw — we serve this area remotely. Emergency and expedited passport services as fast as 24 hours for Kennesaw and Cobb County travelers.",
        url: pageUrl,
        telephone: "+1-877-253-0084",
        areaServed: {
          "@type": "City",
          name: "Kennesaw",
          containedInPlace: {
            "@type": "State",
            name: "Georgia",
          },
        },
        priceRange: "$$$",
        openingHours: ["Mo-Fr 09:00-18:00"],
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "4.9",
          reviewCount: "1200",
        },
      },
      ...servicesList.map((s) => ({
        "@type": "Service",
        serviceType: s.name,
        provider: {
          "@type": "LocalBusiness",
          name: "Fast Passport Center",
        },
        areaServed: {
          "@type": "City",
          name: "Kennesaw",
        },
        description: s.description,
      })),
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.a,
          },
        })),
      },
    ];

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data";
    document.getElementById("structured-data")?.remove();
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);

    return () => {
      const existing = document.getElementById("structured-data");
      if (existing) existing.remove();
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.remove();
    };
  }, []);

  return (
    <div className="font-sans">
      <KennesawHeroSection />
      <KennesawStickyNav />
      <KennesawProcessingTimeSection />
      <KennesawServicesSection />
      <KennesawLocalAreaSection />
      <KennesawTestimonialsSection />
      <KennesawAcceptanceFacilitiesSection />
      <KennesawComparisonSection />
      <KennesawMistakesSection />
      <KennesawHowItWorksSection />
      <KennesawFAQSection />
      <KennesawHelpfulResourcesSection />
      <SEOCrossLinks currentRoute="/cities/kennesaw" state="GA" />
      <KennesawFinalCTA />
    </div>
  );
}